import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, RefreshCw, Server, Wifi, WifiOff, Database,
  ChevronRight, Zap, Clock, Monitor,
} from "lucide-react";
import { tokenStore } from "@/lib/api";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function GaugeArc({ pct, size = 120, color = "#3b82f6" }) {
  // Reliable strokeDasharray approach — unambiguous at ALL fill levels including 0% and 100%.
  // The gauge arc spans 240° starting at the lower-left (SVG rotation 150° CW from 3 o'clock),
  // going clockwise through the top to lower-right, with a 120° gap at the bottom.
  const r          = (size / 2) * 0.76;
  const cx         = size / 2;
  const cy         = size / 2;
  const arcPct     = Math.min(100, Math.max(0, pct));
  const circ       = 2 * Math.PI * r;
  const trackLen   = (240 / 360) * circ;   // 240° track
  const trackGap   = circ - trackLen;
  const fillLen    = (arcPct / 100) * trackLen;
  const fillGap    = circ - fillLen;
  const stroke     = arcPct >= 90 ? "#ef4444" : arcPct >= 70 ? "#f59e0b" : color;
  const startRot   = 150;   // degrees CW from 3 o'clock → positions start at lower-left

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Background track: 240° grey arc */}
      <circle
        cx={cx} cy={cy} r={r}
        fill="none"
        stroke="#e2e8f0"
        strokeWidth={size * 0.07}
        strokeDasharray={`${trackLen} ${trackGap}`}
        strokeLinecap="round"
        transform={`rotate(${startRot}, ${cx}, ${cy})`}
      />
      {/* Coloured progress arc */}
      {arcPct > 0 && (
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={stroke}
          strokeWidth={size * 0.07}
          strokeDasharray={`${fillLen} ${fillGap}`}
          strokeLinecap="round"
          transform={`rotate(${startRot}, ${cx}, ${cy})`}
          style={{ transition: "stroke-dasharray 0.5s ease" }}
        />
      )}
      <text x={cx} y={cy + 4} textAnchor="middle" fontSize={size * 0.18} fontWeight="700"
        fill={arcPct >= 90 ? "#ef4444" : "#1e293b"}>
        {arcPct.toFixed(1)}%
      </text>
      <text x={cx} y={cy + size * 0.18} textAnchor="middle" fontSize={size * 0.1} fill="#64748b">
        fill level
      </text>
    </svg>
  );
}

function PoolCard({ label, pool, maxChunks, isGlobal = false }) {
  if (!pool) return null;
  const chunks  = pool.chunks ?? 0;
  const values  = pool.values ?? 0;
  const bytes   = pool.bytes_raw ?? values * 4;
  const fillPct = pool.fill_pct ?? 0;
  const maxC    = pool.max_chunks ?? maxChunks ?? 5000;

  const status = fillPct >= 90 ? "critical" : fillPct >= 70 ? "warning" : "healthy";
  const statusColor = { critical: "text-red-600 bg-red-50 border-red-200", warning: "text-amber-600 bg-amber-50 border-amber-200", healthy: "text-emerald-700 bg-emerald-50 border-emerald-200" }[status];
  const gaugeColor  = { critical: "#ef4444", warning: "#f59e0b", healthy: "#10b981" }[status];

  return (
    <div className={`bg-white border ${fillPct >= 90 ? "border-red-200 shadow-red-100" : "border-slate-200"} rounded-xl p-5 shadow-sm`} data-testid={`pool-card-${isGlobal ? "global" : label}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-semibold text-slate-700">{isGlobal ? "Global Pool" : label}</span>
            {isGlobal && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">shared</span>}
          </div>
          <p className="text-xs text-slate-400 mt-0.5 ml-6">Redis List · FIFO · {maxC.toLocaleString()} chunks max</p>
        </div>
        <span className={`text-[10px] font-semibold border px-2 py-0.5 rounded-full uppercase tracking-wide ${statusColor}`}>
          {status}
        </span>
      </div>
      <div className="flex items-center gap-6">
        <GaugeArc pct={fillPct} size={110} color={gaugeColor} />
        <div className="space-y-2 flex-1">
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">Chunks stored</span>
            <span className="font-mono font-semibold text-slate-700">{chunks.toLocaleString()} / {maxC.toLocaleString()}</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-1.5">
            <div className={`h-1.5 rounded-full transition-all ${fillPct >= 90 ? "bg-red-500" : fillPct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, fillPct)}%` }} />
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">u32 values</span>
            <span className="font-mono text-slate-600">{values.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">Raw bytes</span>
            <span className="font-mono text-slate-600">{bytes >= 1_000_000 ? `${(bytes / 1_000_000).toFixed(1)} MB` : bytes >= 1000 ? `${(bytes / 1000).toFixed(1)} KB` : `${bytes} B`}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function DeviceRow({ device }) {
  const health = device.last_health;
  const isConnected = true;
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0" data-testid={`connected-device-${device.device_id}`}>
      <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0 animate-pulse" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-mono font-semibold text-slate-700 truncate">{device.device_id}</p>
        <p className="text-[10px] text-slate-400">
          Last seen {device.last_seen ? new Date(device.last_seen).toLocaleTimeString() : "unknown"}
        </p>
      </div>
      {health && (
        <div className="flex items-center gap-3 text-[10px] text-slate-500">
          <span className="flex items-center gap-1">
            <Zap className="w-3 h-3" /> {health.speed || health.speed_mbps || 0} Mbps
          </span>
          <span className="font-mono">{health.health_flags || "0x0001"}</span>
          <span>QCNR {health.qcnr ?? "—"}</span>
        </div>
      )}
    </div>
  );
}

function PushEventRow({ event, idx }) {
  const isReal = !event.simulated;
  return (
    <div
      className={`flex items-center gap-3 py-2 border-b border-slate-100 last:border-0 ${idx === 0 ? "bg-emerald-50/50" : ""}`}
      data-testid={`push-event-${idx}`}
    >
      <div className={`w-2 h-2 rounded-full shrink-0 ${isReal ? "bg-blue-500" : "bg-slate-300"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono font-semibold text-slate-700 truncate">{event.device_id}</span>
          {!isReal && (
            <span className="text-[9px] bg-amber-100 text-amber-700 px-1 rounded font-medium">SIM</span>
          )}
        </div>
        <p className="text-[10px] text-slate-400">{new Date(event.timestamp).toLocaleTimeString()}</p>
      </div>
      <div className="text-right shrink-0 space-y-0.5">
        <p className="text-xs font-mono font-semibold text-slate-700">{event.values_pushed?.toLocaleString()} vals</p>
        <p className="text-[10px] text-slate-400">{(event.bytes_pushed >= 1000 ? `${(event.bytes_pushed / 1000).toFixed(1)} KB` : `${event.bytes_pushed} B`)}</p>
      </div>
      <div className="text-right w-16 shrink-0">
        <div className="text-xs font-mono text-slate-600">{event.pool_fill_pct?.toFixed(1)}%</div>
        <div className="text-[10px] text-slate-400">fill</div>
      </div>
    </div>
  );
}

export default function EntropyPoolDashboard() {
  const [status, setStatus]           = useState(null);
  const [pushHistory, setPushHistory] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const navigate = useNavigate();

  const authHeaders = () => ({ Authorization: `Bearer ${tokenStore.getAccess()}` });

  const fetchAll = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const [statusRes, historyRes] = await Promise.all([
        fetch(`${API}/api/v1/entropy/pool-status`, { headers: authHeaders() }),
        fetch(`${API}/api/v1/entropy/push-history?limit=50`, { headers: authHeaders() }),
      ]);

      if (statusRes.status === 401) { navigate("/login"); return; }

      const [statusData, historyData] = await Promise.all([
        statusRes.json(),
        historyRes.ok ? historyRes.json() : { events: [] },
      ]);

      setStatus(statusData);
      setPushHistory(historyData.events || []);
      setLastRefresh(new Date());
    } catch (e) {
      toast.error("Failed to fetch pool status: " + e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!autoRefresh) return;
    const iv = setInterval(() => fetchAll(), 5000);
    return () => clearInterval(iv);
  }, [autoRefresh, fetchAll]);

  const redis       = status?.redis_pool;
  const mem         = status?.memory_pool;
  const connDevices = status?.connected_devices || [];
  const activeCons  = status?.active_connections ?? 0;

  // Compute throughput summary from history
  const totalValsPushed = pushHistory.reduce((s, e) => s + (e.values_pushed || 0), 0);
  const realEvents      = pushHistory.filter(e => !e.simulated);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="entropy-pool-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-600" />
            Entropy Pool Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Real-time Redis entropy pool — live QRNG hardware device data
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none">
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} className="rounded" />
            Auto-refresh (5s)
          </label>
          <button
            onClick={() => fetchAll(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
            data-testid="refresh-pool-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          {lastRefresh && (
            <span className="text-[10px] text-slate-400 tabular-nums">
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />Loading pool status...
        </div>
      ) : (
        <>
          {/* Redis status + stats row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
            {/* Redis Banner */}
            <div className={`md:col-span-2 flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium ${redis?.available ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="redis-status-banner">
              {redis?.available ? <Wifi className="w-4 h-4 shrink-0" /> : <WifiOff className="w-4 h-4 shrink-0" />}
              <span>{redis?.available ? "Redis connected — live QRNG data" : "Redis unavailable"}</span>
            </div>

            {/* Connected Devices */}
            <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid="connected-devices-count">
              <Monitor className="w-5 h-5 text-blue-500 shrink-0" />
              <div>
                <p className="text-xl font-bold text-slate-900">{activeCons}</p>
                <p className="text-xs text-slate-500">Connected Devices</p>
              </div>
            </div>

            {/* Total Pushes */}
            <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid="total-push-events">
              <Zap className="w-5 h-5 text-amber-500 shrink-0" />
              <div>
                <p className="text-xl font-bold text-slate-900">{pushHistory.length}</p>
                <p className="text-xs text-slate-500">Push Events (last {pushHistory.length})</p>
              </div>
            </div>
          </div>

          {/* Global Pool */}
          {redis?.available && (
            <div className="mb-6">
              <PoolCard label="Global" pool={redis.global} isGlobal />
            </div>
          )}

          {/* Tenant dedicated pools */}
          {redis?.available && redis.tenants && Object.keys(redis.tenants).length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-semibold text-slate-600 mb-3 uppercase tracking-wider">Dedicated Enterprise Pools</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(redis.tenants).map(([tid, pool]) => (
                  <PoolCard key={tid} label={tid} pool={pool} />
                ))}
              </div>
            </div>
          )}

          {/* Main 2-col section: Devices + Push History */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">

            {/* Connected Devices Panel */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="connected-devices-panel">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-slate-400" />
                  <h3 className="text-sm font-semibold text-slate-700">Live Device Connections</h3>
                </div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${activeCons > 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                  {activeCons} active
                </span>
              </div>
              <div className="p-4 min-h-[120px]">
                {connDevices.length > 0 ? (
                  connDevices.map(d => <DeviceRow key={d.device_id} device={d} />)
                ) : (
                  <div className="flex flex-col items-center justify-center h-24 text-slate-400">
                    <WifiOff className="w-6 h-6 mb-2" />
                    <p className="text-xs">No devices currently connected</p>
                    <button
                      onClick={() => navigate("/admin/devices")}
                      className="mt-2 flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                      data-testid="go-to-devices-btn"
                    >
                      Manage QRNG Devices <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Push History Feed */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="push-history-panel">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <h3 className="text-sm font-semibold text-slate-700">Recent Push Activity</h3>
                </div>
                <div className="flex items-center gap-2">
                  {realEvents.length > 0 && (
                    <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                      {realEvents.length} real
                    </span>
                  )}
                  <span className="text-[10px] text-slate-400">
                    {totalValsPushed.toLocaleString()} total vals
                  </span>
                </div>
              </div>
              <div className="p-4 max-h-64 overflow-y-auto">
                {pushHistory.length > 0 ? (
                  pushHistory.slice(0, 20).map((ev, i) => (
                    <PushEventRow key={i} event={ev} idx={i} />
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-24 text-slate-400">
                    <Activity className="w-6 h-6 mb-2" />
                    <p className="text-xs text-center">No push events yet.</p>
                    <p className="text-[10px] text-center mt-1">Connect a QRNG device to see real entropy pushes here.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Memory pool (legacy) */}
          {mem && (
            <div className="mt-2 p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Server className="w-3.5 h-3.5" />In-Memory Pool (Legacy / Fallback)
              </h2>
              <div className="grid grid-cols-3 gap-4 text-xs">
                <div>
                  <p className="text-slate-400">Level</p>
                  <p className={`font-semibold mt-0.5 ${mem.level === "critical" ? "text-red-600" : mem.level === "low" ? "text-amber-600" : "text-emerald-600"}`}>{mem.level?.toUpperCase()}</p>
                </div>
                <div>
                  <p className="text-slate-400">Fill %</p>
                  <p className="font-mono font-semibold text-slate-700 mt-0.5">{(mem.fill_pct ?? mem.fill_percentage)?.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-slate-400">Bytes</p>
                  <p className="font-mono text-slate-600 mt-0.5">{(mem.current_bytes || 0).toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}

          {/* Empty pool notice */}
          {redis?.available && (redis.global?.chunks ?? 0) === 0 && pushHistory.length === 0 && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
              <p className="font-semibold mb-1">Pool is currently empty — no pushes recorded</p>
              <p className="text-xs">Connect a QRNG hardware device to begin ingesting real quantum entropy. The push activity feed will populate automatically.</p>
              <button
                onClick={() => navigate("/admin/devices")}
                className="mt-2 flex items-center gap-1 text-xs font-medium text-amber-800 hover:text-amber-900"
                data-testid="go-to-devices-btn-empty"
              >
                Manage QRNG Devices <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
